import { PoseData, PoseLandmark } from '../types';

/**
 * Motion data for a single limb segment.
 * Contains velocity, normalized intensity, and direction information.
 */
export interface LimbMotionData {
  /** Velocity in pixels per second */
  velocity: number;
  /** Normalized motion intensity [0, 1] where 0 is low motion, 1 is high motion */
  intensity: number;
  /** Unit vector representing direction of motion */
  direction: { x: number; y: number };
}

/**
 * Complete motion data for all tracked limb segments.
 * Provides per-limb velocity and intensity for motion-responsive visualizations.
 */
export interface MotionData {
  leftUpperArm: LimbMotionData;
  leftForearm: LimbMotionData;
  rightUpperArm: LimbMotionData;
  rightForearm: LimbMotionData;
  leftThigh: LimbMotionData;
  leftShin: LimbMotionData;
  rightThigh: LimbMotionData;
  rightShin: LimbMotionData;
  torso: LimbMotionData;
  head: LimbMotionData;
  neck: LimbMotionData;
}

/**
 * Internal structure for tracking limb segment positions and velocities.
 */
interface LimbSegment {
  position: { x: number; y: number };
  velocity: number;
  velocityHistory: number[];
  direction: { x: number; y: number };
}

/**
 * Centralized motion tracking utility that calculates velocity and acceleration per limb.
 * Provides smoothed motion data for artistic visualizations to create motion-responsive effects.
 * 
 * Validates Requirements 3.2.19, 3.5.3
 */
export class MotionTracker {
  private previousPose: PoseData | null = null;
  private previousTimestamp: number = 0;
  private readonly smoothingAlpha: number = 0.3;
  private readonly historyLength: number = 5;
  
  private segments: Map<string, LimbSegment> = new Map();
  
  /**
   * Create a new MotionTracker instance.
   */
  constructor() {
    this.initializeSegments();
  }
  
  /**
   * Initialize all limb segments with default values.
   */
  private initializeSegments(): void {
    const segmentNames = [
      'leftUpperArm',
      'leftForearm',
      'rightUpperArm',
      'rightForearm',
      'leftThigh',
      'leftShin',
      'rightThigh',
      'rightShin',
      'torso',
      'head',
      'neck'
    ];
    
    for (const name of segmentNames) {
      this.segments.set(name, {
        position: { x: 0, y: 0 },
        velocity: 0,
        velocityHistory: [],
        direction: { x: 0, y: 0 }
      });
    }
  }
  
  /**
   * Update motion tracking with new pose data.
   * Calculates velocity for each limb segment and applies smoothing.
   * 
   * @param pose Current pose data
   */
  update(pose: PoseData): void {
    if (!pose.detected) {
      return;
    }
    
    // First frame - initialize segment positions
    if (this.previousPose === null) {
      this.initializeSegmentPositions(pose);
      this.previousPose = pose;
      this.previousTimestamp = pose.timestamp;
      return;
    }
    
    // Calculate time delta in seconds
    const timeDelta = (pose.timestamp - this.previousTimestamp) / 1000;
    if (timeDelta <= 0) {
      return; // Invalid time delta
    }
    
    // Update each limb segment
    this.updateLimbSegment('leftUpperArm', pose.landmarks.leftShoulder, pose.landmarks.leftElbow, timeDelta);
    this.updateLimbSegment('leftForearm', pose.landmarks.leftElbow, pose.landmarks.leftWrist, timeDelta);
    this.updateLimbSegment('rightUpperArm', pose.landmarks.rightShoulder, pose.landmarks.rightElbow, timeDelta);
    this.updateLimbSegment('rightForearm', pose.landmarks.rightElbow, pose.landmarks.rightWrist, timeDelta);
    this.updateLimbSegment('leftThigh', pose.landmarks.leftHip, pose.landmarks.leftKnee, timeDelta);
    this.updateLimbSegment('leftShin', pose.landmarks.leftKnee, pose.landmarks.leftAnkle, timeDelta);
    this.updateLimbSegment('rightThigh', pose.landmarks.rightHip, pose.landmarks.rightKnee, timeDelta);
    this.updateLimbSegment('rightShin', pose.landmarks.rightKnee, pose.landmarks.rightAnkle, timeDelta);
    
    // Torso: midpoint between shoulders and hips
    const shoulderMidpoint = this.getMidpoint(pose.landmarks.leftShoulder, pose.landmarks.rightShoulder);
    const hipMidpoint = this.getMidpoint(pose.landmarks.leftHip, pose.landmarks.rightHip);
    this.updateLimbSegmentFromPoints('torso', shoulderMidpoint, hipMidpoint, timeDelta);
    
    // Head: nose position
    this.updateLimbSegmentFromPoint('head', pose.landmarks.nose, timeDelta);
    
    // Neck: average of head and torso motion
    this.updateNeckMotion(timeDelta);
    
    // Store current pose for next frame
    this.previousPose = pose;
    this.previousTimestamp = pose.timestamp;
  }
  
  /**
   * Initialize segment positions on first frame.
   */
  private initializeSegmentPositions(pose: PoseData): void {
    // Initialize each limb segment position
    this.initializeLimbSegmentPosition('leftUpperArm', pose.landmarks.leftShoulder, pose.landmarks.leftElbow);
    this.initializeLimbSegmentPosition('leftForearm', pose.landmarks.leftElbow, pose.landmarks.leftWrist);
    this.initializeLimbSegmentPosition('rightUpperArm', pose.landmarks.rightShoulder, pose.landmarks.rightElbow);
    this.initializeLimbSegmentPosition('rightForearm', pose.landmarks.rightElbow, pose.landmarks.rightWrist);
    this.initializeLimbSegmentPosition('leftThigh', pose.landmarks.leftHip, pose.landmarks.leftKnee);
    this.initializeLimbSegmentPosition('leftShin', pose.landmarks.leftKnee, pose.landmarks.leftAnkle);
    this.initializeLimbSegmentPosition('rightThigh', pose.landmarks.rightHip, pose.landmarks.rightKnee);
    this.initializeLimbSegmentPosition('rightShin', pose.landmarks.rightKnee, pose.landmarks.rightAnkle);
    
    // Torso
    const shoulderMidpoint = this.getMidpoint(pose.landmarks.leftShoulder, pose.landmarks.rightShoulder);
    const hipMidpoint = this.getMidpoint(pose.landmarks.leftHip, pose.landmarks.rightHip);
    const torsoMidpoint = {
      x: (shoulderMidpoint.x + hipMidpoint.x) / 2,
      y: (shoulderMidpoint.y + hipMidpoint.y) / 2
    };
    const torsoSegment = this.segments.get('torso');
    if (torsoSegment) {
      torsoSegment.position = torsoMidpoint;
    }
    
    // Head
    const headSegment = this.segments.get('head');
    if (headSegment) {
      headSegment.position = { x: pose.landmarks.nose.x, y: pose.landmarks.nose.y };
    }
    
    // Neck: midpoint between head and torso
    const neckSegment = this.segments.get('neck');
    if (neckSegment && headSegment && torsoSegment) {
      neckSegment.position = {
        x: (headSegment.position.x + torsoSegment.position.x) / 2,
        y: (headSegment.position.y + torsoSegment.position.y) / 2
      };
    }
  }
  
  /**
   * Initialize a limb segment's position.
   */
  private initializeLimbSegmentPosition(segmentName: string, start: PoseLandmark, end: PoseLandmark): void {
    const segment = this.segments.get(segmentName);
    if (!segment) {
      return;
    }
    
    // Skip if landmarks have low visibility
    if (start.visibility < 0.5 || end.visibility < 0.5) {
      return;
    }
    
    // Calculate midpoint
    segment.position = this.getMidpoint(start, end);
  }
  
  /**
   * Update a limb segment's motion data.
   * 
   * @param segmentName Name of the limb segment
   * @param start Start landmark of the limb
   * @param end End landmark of the limb
   * @param timeDelta Time since last update in seconds
   */
  private updateLimbSegment(
    segmentName: string,
    start: PoseLandmark,
    end: PoseLandmark,
    timeDelta: number
  ): void {
    // Skip if landmarks have low visibility
    if (start.visibility < 0.5 || end.visibility < 0.5) {
      return;
    }
    
    // Calculate midpoint of limb segment
    const currentPosition = this.getMidpoint(start, end);
    this.updateLimbSegmentFromPoint(segmentName, currentPosition, timeDelta);
  }
  
  /**
   * Update a limb segment from two points (for torso).
   */
  private updateLimbSegmentFromPoints(
    segmentName: string,
    start: { x: number; y: number },
    end: { x: number; y: number },
    timeDelta: number
  ): void {
    const currentPosition = {
      x: (start.x + end.x) / 2,
      y: (start.y + end.y) / 2
    };
    this.updateLimbSegmentFromPoint(segmentName, currentPosition, timeDelta);
  }
  
  /**
   * Update a limb segment from a single point (for head).
   */
  private updateLimbSegmentFromPoint(
    segmentName: string,
    currentPosition: { x: number; y: number } | PoseLandmark,
    timeDelta: number
  ): void {
    const segment = this.segments.get(segmentName);
    if (!segment) {
      return;
    }
    
    // Skip if landmark has low visibility (for PoseLandmark)
    if ('visibility' in currentPosition && currentPosition.visibility < 0.5) {
      return;
    }
    
    const pos = { x: currentPosition.x, y: currentPosition.y };
    
    // Calculate distance moved (Euclidean distance)
    const dx = pos.x - segment.position.x;
    const dy = pos.y - segment.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Calculate raw velocity (pixels per second)
    // Assuming normalized coordinates are in [0, 1] range, scale to screen pixels
    // Using a reference screen width of 1920 pixels for velocity calculation
    const referenceWidth = 1920;
    const rawVelocity = (distance * referenceWidth) / timeDelta;
    
    // Apply exponential moving average for smoothing.
    // Seed with the first measured velocity instead of ramping from zero, so a
    // fast initial motion is not under-reported during a startup ramp. Subsequent
    // frames blend toward the raw value at the smoothing rate.
    const smoothedVelocity = segment.velocityHistory.length === 0
      ? rawVelocity
      : this.smoothingAlpha * rawVelocity + (1 - this.smoothingAlpha) * segment.velocity;
    
    // Update velocity history
    segment.velocityHistory.push(smoothedVelocity);
    if (segment.velocityHistory.length > this.historyLength) {
      segment.velocityHistory.shift();
    }
    
    // Calculate direction (unit vector)
    let direction = { x: 0, y: 0 };
    if (distance > 0.001) { // Avoid division by zero
      direction = {
        x: dx / distance,
        y: dy / distance
      };
    }
    
    // Update segment
    segment.position = pos;
    segment.velocity = smoothedVelocity;
    segment.direction = direction;
  }
  
  /**
   * Update neck motion as average of head and torso motion.
   * Neck motion intensity is calculated as the average of head and torso intensities.
   * 
   * @param _timeDelta Time since last update in seconds (unused, kept for consistency)
   */
  private updateNeckMotion(_timeDelta: number): void {
    const headSegment = this.segments.get('head');
    const torsoSegment = this.segments.get('torso');
    const neckSegment = this.segments.get('neck');
    
    if (!headSegment || !torsoSegment || !neckSegment) {
      return;
    }
    
    // Calculate average velocity
    const avgVelocity = (headSegment.velocity + torsoSegment.velocity) / 2;
    
    // Apply smoothing to neck velocity (seed with first value, see updateLimbSegmentFromPoint)
    const smoothedVelocity = neckSegment.velocityHistory.length === 0
      ? avgVelocity
      : this.smoothingAlpha * avgVelocity + (1 - this.smoothingAlpha) * neckSegment.velocity;
    
    // Update velocity history
    neckSegment.velocityHistory.push(smoothedVelocity);
    if (neckSegment.velocityHistory.length > this.historyLength) {
      neckSegment.velocityHistory.shift();
    }
    
    // Calculate average direction
    const dx = (headSegment.direction.x + torsoSegment.direction.x) / 2;
    const dy = (headSegment.direction.y + torsoSegment.direction.y) / 2;
    const magnitude = Math.sqrt(dx * dx + dy * dy);
    
    let direction = { x: 0, y: 0 };
    if (magnitude > 0.001) {
      direction = {
        x: dx / magnitude,
        y: dy / magnitude
      };
    }
    
    // Calculate average position (midpoint between head and torso)
    const position = {
      x: (headSegment.position.x + torsoSegment.position.x) / 2,
      y: (headSegment.position.y + torsoSegment.position.y) / 2
    };
    
    // Update neck segment
    neckSegment.position = position;
    neckSegment.velocity = smoothedVelocity;
    neckSegment.direction = direction;
  }
  
  /**
   * Calculate midpoint between two landmarks.
   */
  private getMidpoint(a: PoseLandmark, b: PoseLandmark): { x: number; y: number } {
    return {
      x: (a.x + b.x) / 2,
      y: (a.y + b.y) / 2
    };
  }
  
  /**
   * Get current motion data for all limb segments.
   * 
   * @returns Complete motion data with velocity and intensity for each limb
   */
  getMotionData(): MotionData {
    return {
      leftUpperArm: this.getLimbMotionData('leftUpperArm'),
      leftForearm: this.getLimbMotionData('leftForearm'),
      rightUpperArm: this.getLimbMotionData('rightUpperArm'),
      rightForearm: this.getLimbMotionData('rightForearm'),
      leftThigh: this.getLimbMotionData('leftThigh'),
      leftShin: this.getLimbMotionData('leftShin'),
      rightThigh: this.getLimbMotionData('rightThigh'),
      rightShin: this.getLimbMotionData('rightShin'),
      torso: this.getLimbMotionData('torso'),
      head: this.getLimbMotionData('head'),
      neck: this.getLimbMotionData('neck')
    };
  }
  
  /**
   * Get motion data for a specific limb segment.
   */
  private getLimbMotionData(segmentName: string): LimbMotionData {
    const segment = this.segments.get(segmentName);
    if (!segment) {
      return {
        velocity: 0,
        intensity: 0,
        direction: { x: 0, y: 0 }
      };
    }
    
    return {
      velocity: segment.velocity,
      intensity: this.calculateIntensity(segment.velocity),
      direction: segment.direction
    };
  }
  
  /**
   * Map velocity to normalized intensity [0, 1].
   * Low motion threshold: < 50 pixels/second → intensity 0
   * High motion threshold: > 500 pixels/second → intensity 1
   * Linear interpolation between thresholds.
   * 
   * @param velocity Velocity in pixels per second
   * @returns Normalized intensity [0, 1]
   */
  private calculateIntensity(velocity: number): number {
    const lowThreshold = 50;
    const highThreshold = 500;
    
    // Handle edge cases
    if (isNaN(velocity) || velocity < 0) {
      return 0;
    }
    
    if (velocity <= lowThreshold) {
      return 0;
    }
    
    if (velocity >= highThreshold) {
      return 1;
    }
    
    // Linear interpolation
    return (velocity - lowThreshold) / (highThreshold - lowThreshold);
  }
  
  /**
   * Reset motion tracking state.
   * Clears all history and previous pose data.
   */
  reset(): void {
    this.previousPose = null;
    this.previousTimestamp = 0;
    this.initializeSegments();
  }
}
